import * as T from 'three';

type Driver={bone:T.Bone;point:T.Vector3;restPoint:T.Vector3;restRotationInverse:T.Quaternion;position:T.Vector3;rotation:T.Vector3};

/** Small anatomical offsets in the wing's torso frame, relative to the rig bind.
 * Shoulder sockets, mid-spine and proximal hips contribute independently. Bone
 * axes come from inverse binds; a whole-player turn/tilt creates no extra motion.
 * These are formation targets, followed by the existing ~100 ms wing springs. */
export class CrystalWingBodyPose {
  readonly positions=Array.from({length:6},()=>new T.Vector3());
  readonly rotations=Array.from({length:6},()=>new T.Quaternion());
  enabled=true;
  bound=false;
  private shoulders:Driver[]=[];
  private hips:Driver[]=[];
  private spine:Driver|null=null;
  private pelvis:Driver|null=null;
  private readonly inverse=new T.Quaternion();
  private readonly delta=new T.Quaternion();
  private readonly matrix=new T.Matrix4();
  private readonly axis=new T.Vector3();
  private readonly lever=new T.Vector3();
  private readonly restLever=new T.Vector3();

  reset():void{
    this.bound=false;this.shoulders.length=0;this.hips.length=0;this.spine=this.pelvis=null;
    for(let i=0;i<6;i++){this.positions[i].set(0,0,0);this.rotations[i].identity();}
  }

  bind(bones:ReadonlyMap<string,T.Bone>,skeleton:T.Skeleton,scale=1):void{
    this.reset();
    const bind=(name:string):T.Matrix4|null=>{
      const bone=bones.get(name),index=bone?skeleton.bones.indexOf(bone):-1;
      return index<0?null:skeleton.boneInverses[index].clone().invert();
    };
    const hip=bind('Hips'),head=bind('Head');if(!hip||!head)return;
    const center=new T.Vector3().setFromMatrixPosition(hip),up=new T.Vector3().setFromMatrixPosition(head);
    center.add(up).multiplyScalar(.5);up.sub(new T.Vector3().setFromMatrixPosition(hip)).normalize();
    // Avatar bind space has game X right, the same reference used by
    // flightAnchorInto. Reconstruct its neutral torso frame from the bind.
    const right=new T.Vector3(1,0,0),back=new T.Vector3().crossVectors(right,up).normalize();
    up.crossVectors(back,right).normalize();
    const inverse=new T.Quaternion().setFromRotationMatrix(new T.Matrix4().makeBasis(right,up,back)).invert();
    const make=(name:string,end?:string,fraction=1):Driver|null=>{
      const bone=bones.get(name),world=bind(name);if(!bone||!world)return null;
      const point=new T.Vector3().setFromMatrixPosition(world),endpoint=end?bind(end):null;
      if(endpoint)point.lerp(new T.Vector3().setFromMatrixPosition(endpoint),fraction);
      return {bone,point:point.clone().applyMatrix4(world.clone().invert()),restPoint:point.sub(center).applyQuaternion(inverse).divideScalar(scale),
        restRotationInverse:new T.Quaternion().setFromRotationMatrix(this.matrix.extractRotation(world)).premultiply(inverse).invert(),position:new T.Vector3(),rotation:new T.Vector3()};
    };
    const shoulders=[make('LeftShoulder','LeftArm'),make('RightShoulder','RightArm')];
    // Sample only the proximal 12% of each thigh: hip/gait response, not a wing
    // attached to the knee. Pelvis rotation supplies most of its angular motion.
    const hips=[make('LeftUpLeg','LeftLeg',.12),make('RightUpLeg','RightLeg',.12)];
    this.spine=make('Spine1');this.pelvis=make('Hips');
    if(shoulders.some(s=>!s)||hips.some(s=>!s)||!this.spine||!this.pelvis){this.reset();return;}
    // Sort by physical side, not bone naming conventions (imported rigs differ).
    this.shoulders=(shoulders as Driver[]).sort((a,b)=>a.restPoint.x-b.restPoint.x);
    this.hips=(hips as Driver[]).sort((a,b)=>a.restPoint.x-b.restPoint.x);
    this.bound=true;
  }

  update(center:T.Vector3,torso:T.Quaternion,scale=1):void{
    if(!this.bound)return;
    this.inverse.copy(torso).invert();
    const sample=(driver:Driver):void=>{
      driver.position.copy(driver.point).applyMatrix4(driver.bone.matrixWorld).sub(center).applyQuaternion(this.inverse).divideScalar(scale).sub(driver.restPoint);
      driver.bone.getWorldQuaternion(this.delta);this.delta.premultiply(this.inverse).multiply(driver.restRotationInverse).normalize();
      if(this.delta.w<0)this.delta.set(-this.delta.x,-this.delta.y,-this.delta.z,-this.delta.w);
      const sine=Math.hypot(this.delta.x,this.delta.y,this.delta.z),angle=2*Math.atan2(sine,this.delta.w);
      driver.rotation.set(this.delta.x,this.delta.y,this.delta.z).multiplyScalar(sine>1e-8?Math.min(angle,1.2)/sine:2);
    };
    sample(this.spine!);sample(this.pelvis!);
    for(let side=0;side<2;side++){
      const shoulder=this.shoulders[side],hip=this.hips[side],spine=this.spine!,pelvis=this.pelvis!;
      sample(shoulder);sample(hip);
      for(let rank=0;rank<3;rank++){
        const i=side*3+rank,p=this.positions[i],q=this.rotations[i];
        if(rank===0){
          p.copy(shoulder.position).multiplyScalar(.48).addScaledVector(spine.position,.08);
          this.axis.copy(shoulder.rotation).multiplyScalar(.28).addScaledVector(spine.rotation,.04);
        }else if(rank===1){
          p.copy(spine.position).multiplyScalar(.46).addScaledVector(shoulder.position,.12);
          this.axis.copy(spine.rotation).multiplyScalar(.26).addScaledVector(shoulder.rotation,.065);
        }else{
          p.copy(hip.position).multiplyScalar(.30).addScaledVector(pelvis.position,.08);
          this.axis.copy(pelvis.rotation).multiplyScalar(.18).addScaledVector(hip.rotation,.055);
        }
        const angle=this.axis.length();
        q.setFromAxisAngle(this.axis.multiplyScalar(angle>1e-8?1/angle:0),Math.min(angle,[.18,.14,.12][rank]));
        // A short lever makes spine/hip torsion move the row, as well as rotate
        // its facets. It is deliberately much shorter than the full wing span.
        this.restLever.set((side===0?-1:1)*[.09,.18,.12][rank],0,.06);
        p.add(this.lever.copy(this.restLever).applyQuaternion(q).sub(this.restLever));
        p.clampLength(0,[.060,.045,.040][rank]);
        // Body motion may lift a crystal off the back; inward travel is smaller.
        p.z=Math.max(p.z,-.018);
      }
    }
  }
}
